/**
 * Search Modal Utility
 * 
 * Handles search modal functionality with Shopify Predictive Search API
 */

import { gsap } from "gsap";

export class SearchModal {
  constructor() {
    this.modal = document.querySelector('.search-modal');
    this.overlay = this.modal?.querySelector('.search-modal__overlay');
    this.modalContainer = this.modal?.querySelector('.search-modal__container');
    this.input = this.modal?.querySelector('.search-modal__input');
    this.resultsContainer = this.modal?.querySelector('.search-modal__results');
    this.seeAllLink = this.modal?.querySelector('.search-modal__see-all');
    this.closeButton = this.modal?.querySelector('.search-modal__close');
    this.isOpen = false;
    this.debounceTimer = null;
    this.minChars = 3;
    this.debounceDelay = 300;
    this.maxResults = 4;
    this.searchTypes = 'product,article,page';

    if (this.modal) {
      this.init();
      this.events();
    }
  }

  init = () => {
    // Set initial state
    gsap.set(this.modalContainer, { y: '-100%', opacity: 0 });
    gsap.set(this.overlay, { opacity: 0 });
    this.modal.style.visibility = 'hidden';
    this.modal.style.pointerEvents = 'none';
  }

  events = () => {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close);
    }

    // Input debounce
    if (this.input) {
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close);
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  handleInput = (e) => {
    const query = e.target.value.trim();

    // Clear previous debounce
    clearTimeout(this.debounceTimer);

    // Clear results if query is too short
    if (query.length < this.minChars) {
      this.clearResults();
      return;
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, this.debounceDelay);
  }

  handleKeydown = (e) => {
    // Allow Enter to submit (navigate to search page)
    if (e.key === 'Enter' && this.input.value.trim().length >= this.minChars) {
      e.preventDefault();
      window.location.href = `/search?q=${encodeURIComponent(this.input.value.trim())}`;
    }
  }

  async fetchResults(query) {
    if (!query || query.length < this.minChars) return;

    try {
      this.showLoading();

      const q = encodeURIComponent(query);
      const [sectionResponse, countResponse] = await Promise.all([
        fetch(`/search/suggest?q=${q}&resources[type]=${this.searchTypes}&resources[limit]=${this.maxResults}&section_id=predictive-search`),
        fetch(`/search?q=${q}&type=${this.searchTypes}&view=count`)
      ]);

      if (!sectionResponse.ok) {
        throw new Error('Search request failed');
      }

      const html = await sectionResponse.text();
      let totalCount = null;
      if (countResponse.ok) {
        try {
          totalCount = (await countResponse.json())?.count;
        } catch (e) {
          // Count is non-essential — fall back to results length
        }
      }
      this.renderResults(html, totalCount);
    } catch (error) {
      console.error('Error fetching search results:', error);
      this.showError();
    }
  }

  renderResults(html, totalCount = null) {
    if (!this.resultsContainer) return;

    // Shopify wraps section output in <div id="shopify-section-...">.
    // Extract just the cards inside.
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const sectionWrapper = doc.querySelector('[id^="shopify-section-"]');
    const inner = (sectionWrapper ? sectionWrapper.innerHTML : html).trim();

    if (!inner) {
      this.resultsContainer.innerHTML = `
        <div class="search-modal__empty">
          <p>No results found</p>
        </div>
      `;
      this.hideSeeAll();
      return;
    }

    this.resultsContainer.innerHTML = inner;
    this.showSeeAll(totalCount);
  }

  showLoading() {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = `
      <div class="search-modal__loading">
        <p>Searching...</p>
      </div>
    `;
    this.hideSeeAll();
  }

  showError() {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = `
      <div class="search-modal__error">
        <p>Error loading results. Please try again.</p>
      </div>
    `;
    this.hideSeeAll();
  }

  showSeeAll(count = null) {
    if (!this.seeAllLink) return;
    const query = this.input?.value.trim() || '';
    this.seeAllLink.href = `/search?q=${encodeURIComponent(query)}&type=${this.searchTypes}`;
    const label = this.seeAllLink.querySelector('span');
    if (label) {
      label.textContent = count != null
        ? `SEE ALL ${count} RESULTS`
        : 'SEE ALL RESULTS';
    }
    this.seeAllLink.hidden = false;
  }

  hideSeeAll() {
    if (this.seeAllLink) {
      this.seeAllLink.hidden = true;
    }
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
    this.hideSeeAll();
  }

  open = () => {
    if (this.isOpen) return;

    this.isOpen = true;
    this.modal.style.visibility = 'visible';
    this.modal.style.pointerEvents = 'auto';
    document.body.style.overflow = 'hidden';

    // Animate overlay
    gsap.to(this.overlay, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out'
    });

    // Animate modal container
    gsap.to(this.modalContainer, {
      y: '0%',
      opacity: 1,
      duration: 0.7,
      ease: 'expo.out'
    });

    // Focus input after animation
    setTimeout(() => {
      if (this.input) {
        this.input.focus();
      }
    }, 100);
  }

  close = () => {
    if (!this.isOpen) return;

    this.isOpen = false;
    document.body.style.overflow = '';

    // Animate overlay out
    gsap.to(this.overlay, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in'
    });

    // Animate modal container out
    gsap.to(this.modalContainer, {
      y: '-100%',
      opacity: 0,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        this.modal.style.visibility = 'hidden';
        this.modal.style.pointerEvents = 'none';
        // Clear input and results
        if (this.input) {
          this.input.value = '';
        }
        this.clearResults();
      }
    });
  }
}

export default SearchModal;

